import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import loadable from 'components/utils/lazyImport'
import MarkdownRender from 'LuxKit/MarkdownRender'
import NavigationBar from './NavigableOfferContentBar'
import PropertyReview from './PropertyReview'
import LayoutContainer from 'components/Common/LayoutContainer'
import { PROPERTY_MAP_MARKER_TYPE } from 'constants/map'
import FinePrint from '../../Common/FinePrint'
import ThingsToKnow from '../ThingsToKnow/ThingsToKnow'
import TraderInformation from './TraderInformation'
import BodyText from 'LuxKit/Typography/BodyText'
import NavigableOfferContentGroup from './NavigableOfferContentGroup'
import Modal from 'LuxKit/Modal/Modal'
import TextLink from 'LuxKit/TextLink'
import UserReviewList from './UserReviewList'
import { isBundleOffer } from 'lib/offer/offerTypes'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'LuxKit/Divider'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import { scrollToId } from 'lib/window/scrollUtils'
import UserReviewSection from './UserReviewSection'
import { Experiments } from 'constants/optimizely'
import { trackClientEvent } from 'analytics/analytics'
import { useInView } from 'react-intersection-observer'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'

const PropertyMap = loadable(() => import(/* webpackChunkName: "PropertyMap" */ 'components/Common/PropertyMap'))

interface Props {
  hash?: string
  offerDetails: App.Offer
  traderInformation?: App.TraderInformation
  mobileHotelOfferPageTest?: boolean
}

const StyledFacilities = styled(MarkdownRender)`
  > ul {
    columns: auto 2;
  }
`

const READ_MORE_TRACKING_MAP: Record<string, string> = {
  'getting-there': 'offer_page_read_more_location',
  highlights: 'offer_page_read_more_highlights',
  facilities: 'offer_page_read_more_facilities',
  'fine-print': 'offer_page_read_more_fine_print',
}

function packageHasPropertyWithCoords(offer: App.Offer) {
  return !!offer.property?.latitude && !!offer.property?.longitude
}

interface ContentField {
  id: string
  label: string
  open: boolean
  collapsable: boolean
  initialHeight?: number
  minimumContentHeight?: number
  contentComponent: React.ReactElement | Array<React.ReactElement>
}

interface ContentFieldsOptions {
  isNewReviewsPanelEnabled: boolean
  shouldHideHighlights: boolean
  isStructuredFinePrintEnabled: boolean
  intl: ReturnType<typeof useIntl>
}

function getContentFields(props: Props, options: ContentFieldsOptions): Array<ContentField> {
  const {
    hash,
    offerDetails,
    mobileHotelOfferPageTest,
  } = props

  const { isNewReviewsPanelEnabled, shouldHideHighlights, isStructuredFinePrintEnabled, intl } = options

  const {
    highlights,
    facilities,
    finePrint,
    gettingThere,
    lowestPricePackage,
    property,
  } = offerDetails

  const contentFields: Array<ContentField> = []
  if (packageHasPropertyWithCoords(offerDetails)) {
    const markers: Array<App.MapMarker> = []

    if (isBundleOffer(offerDetails)) {
      const bundleOffers = Object.values(offerDetails.bundledOffers)
      bundleOffers.forEach((bundleOffer) => {
        markers.push({
          id: bundleOffer.property.id,
          type: PROPERTY_MAP_MARKER_TYPE,
          latitude: bundleOffer.property.latitude,
          longitude: bundleOffer.property.longitude,
        })
      })
    } else {
      if (property?.latitude && property?.longitude) {
        markers.push({
          id: property.id,
          type: PROPERTY_MAP_MARKER_TYPE,
          latitude: property.latitude,
          longitude: property.longitude,
        })
      }
    }

    const bundledOffers = Object.values(offerDetails.bundledOffers ?? {})

    contentFields.push({
      id: 'getting-there',
      label: intl.formatMessage({
        id: 'fResOSmaDC',
        defaultMessage: 'Location',
        description: 'Label for location section',
      }),
      open: hash === '#getting-there',
      collapsable: true,
      initialHeight: 410,
      contentComponent: <Group gap={16} direction="vertical">
        <PropertyMap
          height={258}
          wrapperStyle={{ height: 258 }}
          markers={markers}
        />
        {isBundleOffer(offerDetails) && <Group gap={16} direction="vertical">
          <div>
            <BodyText variant="medium">{bundledOffers[0].property?.name}</BodyText>
            <BodyText variant="medium" weight="bold">{bundledOffers[0].property?.address}</BodyText>
          </div>
          <div>
            <BodyText variant="medium">{bundledOffers[1].property?.name}</BodyText>
            <BodyText variant="medium" weight="bold">{bundledOffers[1].property?.address}</BodyText>
          </div>
        </Group>}
        {!isBundleOffer(offerDetails) && <div>
          <BodyText variant="medium">{property?.name}</BodyText>
          <BodyText variant="medium" weight="bold">{property?.address}</BodyText>
        </div>}
        <MarkdownRender fontSize="medium" type="compact" content={gettingThere} />
      </Group>,
    })
  }

  if (highlights && !mobileHotelOfferPageTest && !shouldHideHighlights) {
    contentFields.push({
      id: 'highlights',
      label: intl.formatMessage({
        id: 'mVnkv7aDxp',
        defaultMessage: 'Highlights',
        description: 'Label for highlights section',
      }),
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={highlights} />,
      open: hash === '#highlights',
    })
  }

  if (facilities && !mobileHotelOfferPageTest) {
    contentFields.push({
      id: 'facilities',
      label: intl.formatMessage({
        id: 't9IN1H5/gm',
        defaultMessage: 'Facilities',
        description: 'Label for facilities section',
      }),
      open: hash === '#facilities',
      collapsable: true,
      initialHeight: 120,
      contentComponent: <StyledFacilities type="compact" fontSize="medium" content={facilities} />,
    })
  }

  if (gettingThere && !packageHasPropertyWithCoords(offerDetails)) {
    contentFields.push({
      id: 'getting-there',
      label: intl.formatMessage({
        id: 'fResOSmaDC',
        defaultMessage: 'Location',
        description: 'Label for location section',
      }),
      open: hash === '#getting-there',
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={gettingThere} />,
    })
  }

  if (lowestPricePackage?.tour?.itinerary) {
    contentFields.push({
      id: 'itinerary',
      label: intl.formatMessage({
        id: '/FoJKxvAFx',
        defaultMessage: 'Itinerary',
        description: 'Label for itinerary section',
      }),
      open: hash === '#itinerary',
      collapsable: true,
      contentComponent: <MarkdownRender type="compact" fontSize="medium" content={lowestPricePackage.tour.itinerary} />,
    })
  }

  if (isStructuredFinePrintEnabled && (finePrint || offerDetails.inclusions || offerDetails.facilities)) {
    contentFields.push({
      id: 'fine-print',
      label: intl.formatMessage({
        id: 'L2jyO6rzru',
        defaultMessage: 'Things to know',
        description: 'Label for things to know section',
      }),
      open: !!offerDetails.property?.taxesAndFeesContent || hash === '#fine-print',
      collapsable: false,
      contentComponent: <ThingsToKnow offer={offerDetails} />,
    })
  } else if (finePrint) {
    contentFields.push({
      id: 'fine-print',
      label: intl.formatMessage({
        id: '/uEg8G2f9N',
        defaultMessage: 'Fine Print',
        description: 'Label for fine print section',
      }),
      open: !!offerDetails.property?.taxesAndFeesContent || hash === '#fine-print',
      collapsable: true,
      contentComponent: <FinePrint finePrint={finePrint} offer={offerDetails} />,
    })
  }

  if (lowestPricePackage?.tour?.reviews.length) {
    contentFields.push({
      id: 'customer-reviews',
      label: intl.formatMessage({
        id: 'jpF8L5Tx8a',
        defaultMessage: 'Reviews',
        description: 'Label for reviews section',
      }),
      open: hash === '#customer-reviews',
      collapsable: false,
      contentComponent: lowestPricePackage.tour.reviews.map(review =>
        <PropertyReview
          key={review.id}
          content={review.content}
          source={review.source}
        />,
      ),
    })
  }

  if (!isBundleOffer(offerDetails) && property?.rating?.reviewsTotal && property?.rating.score) {
    contentFields.push({
      id: 'customer-reviews',
      label: intl.formatMessage({
        id: 'jpF8L5Tx8a',
        defaultMessage: 'Reviews',
        description: 'Label for reviews section',
      }),
      open: hash === '#customer-reviews',
      collapsable: false,
      contentComponent: isNewReviewsPanelEnabled ? <UserReviewSection property={property} /> : <UserReviewList property={property} />,
    })
  }

  return contentFields
}

function NavigableOfferContent(props: Props) {
  const intl = useIntl()
  const previewMode = useAppSelector(state => state.system.previewMode)
  const { isTurnedOn: isNewReviewsPanelEnabled, triggerActivation: triggerReviewsExperiment } =
    useExperiment(Experiments.croNewReviewsPanelV2, { canActivate: false })

  const { isAnyVariant: isStructuredFinePrintEnabled, triggerActivation: onFinePrintSectionViewed } =
    useExperiment(Experiments.croStructuredFinePrint, { canActivate: false })

  const hasHighlights = !!props.offerDetails.highlights
  const isFlashHotelOffer = props.offerDetails.type === OFFER_TYPE_HOTEL
  const shouldTriggerRemoveHighlightsExperiment = isFlashHotelOffer && hasHighlights && !previewMode
  const [navBarRef, navBarInView] = useInView({ triggerOnce: true, skip: !shouldTriggerRemoveHighlightsExperiment })
  const { isAnyVariant: removeHighlightsVariationEnabled } =
    useExperiment(Experiments.croRemoveFlashHighlightsContentSection, { canActivate: navBarInView && shouldTriggerRemoveHighlightsExperiment })
  const shouldHideHighlights = isFlashHotelOffer && hasHighlights && removeHighlightsVariationEnabled && !previewMode

  const { hash, offerDetails, mobileHotelOfferPageTest } = props
  const contentFields = useMemo(
    () => getContentFields(props, { isNewReviewsPanelEnabled, shouldHideHighlights, isStructuredFinePrintEnabled, intl }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- destructured props fields used as stable deps
    [hash, offerDetails, mobileHotelOfferPageTest, isNewReviewsPanelEnabled, shouldHideHighlights, isStructuredFinePrintEnabled, intl],
  )
  const defaultOpenField = contentFields.find(field => field.open)

  const [openFields, setOpenFields] = useState<Record<string, boolean>>(defaultOpenField ? { [defaultOpenField.id]: true } : {})
  const [headerSelected, setHeaderSelected] = useState<string | undefined>(defaultOpenField ? defaultOpenField?.id : undefined)
  const onGroupInView = useCallback((id: string, inView: boolean) => {
    if (inView) {
      setHeaderSelected(id)
      if (id === 'customer-reviews') {
        triggerReviewsExperiment()
      }
      if (id === 'fine-print') {
        onFinePrintSectionViewed()
      }
    }
  }, [triggerReviewsExperiment, onFinePrintSectionViewed])

  const setFieldToggle = useCallback((id: string, toggled: boolean) => {
    window.setTimeout(() => {
      scrollToId(id, -71)
    }, 300)

    setOpenFields({
      ...openFields,
      [id]: toggled,
    })
  }, [openFields])

  const onHeaderClick = useCallback((id: string) => {
    setFieldToggle(id, true)
  }, [setFieldToggle])

  const onReadMoreToggle = useCallback((id: string) => {
    if (READ_MORE_TRACKING_MAP[id]) {
      const isOpening = !openFields[id]
      const subject = isOpening ? READ_MORE_TRACKING_MAP[id] : `${READ_MORE_TRACKING_MAP[id]}_close`
      trackClientEvent({
        subject,
        action: 'click',
        category: 'offer page',
        type: 'interaction',
      })
    }
    setFieldToggle(id, !openFields[id])
  }, [setFieldToggle, openFields])

  const showModal = useContext(ModalContext)

  const onModalOpenToggle = useCallback(() => {
    showModal(<Modal
      title={intl.formatMessage({
        id: 'PE4Pr9yjI7',
        defaultMessage: 'About this supplier',
        description: 'Title for supplier information modal',
      })}
    >
      <TraderInformation traderInformation={props.traderInformation!} />
    </Modal>)
  }, [showModal, props.traderInformation, intl])

  return (
    <>
      <div ref={navBarRef}>
        <NavigationBar
          contentFields={contentFields}
          onClick={onHeaderClick}
          selectedHeader={headerSelected}
        />
      </div>
      <LayoutContainer variant="large">
        <Sheet padding="20 0" tabletPadding="56 0">
          <VerticalSpacer gap={20}>
            {contentFields.map((contentField, index) => <React.Fragment key={contentField.id}>
              {index !== 0 && <Divider kind="secondary" direction="horizontal" />}
              <NavigableOfferContentGroup
                heading={contentField.label}
                id={contentField.id}
                onInViewChange={onGroupInView}
                initialHeight={contentField.initialHeight ?? 96}
                minimumContentHeight={contentField.minimumContentHeight ?? 0}
                open={!!openFields[contentField.id]}
                onToggle={onReadMoreToggle}
                collapsable={contentField.collapsable}
              >
                <BodyText variant="medium">
                  {contentField.contentComponent}
                </BodyText>
              </NavigableOfferContentGroup>
            </React.Fragment>)}
            {props.traderInformation && (
              <div id="trader-information">
                <TextLink size="medium" onClick={onModalOpenToggle}>
                  {intl.formatMessage({
                    id: 'WCeBISRWTJ',
                    defaultMessage: 'About this supplier',
                    description: 'Link text to view supplier information',
                  })}
                </TextLink>
              </div>
            )}
          </VerticalSpacer>
        </Sheet>
      </LayoutContainer>
    </>
  )
}

export default NavigableOfferContent
